import { useRef } from 'react';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroRichText,
} from '../../shared/components';
import { DataRoom, UpdateDataRoomDto } from '../../shared/models';
import { useApi } from '../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { Formik } from 'formik';
import { Grid } from '@mui/material';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import { QueryKeys } from '../../shared/hooks';

interface InviteSummaryDrawerProps {
	open: boolean;
	dataRoom: DataRoom;
	onClose: () => void;
}

export const InviteSummaryDrawer: React.FC<InviteSummaryDrawerProps> = ({ open, dataRoom, onClose }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const ref = useRef<HTMLDivElement>(null);

	const summaryMutation = useMutation(
		async ({ values }: InviteSummaryFormMutationVariables) => {
			const dataRoomPatch: UpdateDataRoomDto = {
				roomSummaryText: values.summaryText,
				page: 'Summary',
			};

			const { data, error } = await getApiResponseData<DataRoom>(
				api.patch(CerebroApiEndpoints.DataRooms.Root(dataRoom.guid), dataRoomPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return { data };
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.LenderInvites.Root(dataRoom.guid));
				snackbar.enqueueSnackbar('Data room was updated.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<Formik
				initialValues={{
					summaryText: dataRoom.roomSummaryText,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(summaryMutation.mutate)}
			>
				{({ isSubmitting, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm
							flatIndicator
							encType="multipart/form-data"
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
							scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel id="summaryText">Data Room Summary/Lender Invite Text</CerebroLabel>
									<CerebroRichText
										placeholder="Enter Summary Text Here..."
										value={values.summaryText ?? ''}
										onChange={updatedValue => setFieldValue('summaryText', updatedValue)}
									/>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type InviteSummaryFormMutationVariables = FormikUtils.MutateOnSubmitVariables<InviteSummaryFormValues>;
interface InviteSummaryFormValues {
	summaryText: string;
}
